import type { ReactFlowNode } from '../../types';
import type { Editor } from '@tiptap/react';
import type { Attrs } from '@tiptap/pm/model';
import type {
  TextTemplateChunk,
  VariableRef,
} from '@/proto/logiblocks/logiblocks';
import { VariableSource } from '@/proto/common';
import { parseVariableRefName, variableRefName } from '../variableRefs';
import { formatVariableRef } from '../variableUtils';

/** `{{node.name}}`, spaces allowed inside the braces, as the backend reads it. */
const VARIABLE_REFERENCE =
  /\{\{\s*(?<nodeId>[^.{}\s]+)\.(?<name>[^{}\s]+)\s*\}\}/;

/** `@[userId:label]`, a person picked from the editor's @ list. */
const USER_MENTION = /@\[(?<userId>[^:\]]+):(?<label>[^\]]+)\]/;

/** Either part, wherever it appears in the text. */
const TEMPLATE_PART_PATTERN = `${VARIABLE_REFERENCE.source}|${USER_MENTION.source}`;

/**
 * Appends a run of plain text as a chunk. Returns false when the run holds a
 * `{{` that the reference form didn't consume, so the caller can reject the
 * template rather than send braces as literal text.
 */
function maybeAppendTextChunk(
  chunks: TextTemplateChunk[],
  text: string
): boolean {
  if (text.includes('{{')) {
    return false;
  }
  if (text !== '') {
    chunks.push({ text });
  }
  return true;
}

function escapeHTML(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(str: string): string {
  return escapeHTML(str).replace(/"/g, '&quot;');
}

/**
 * Protects a line's leading run of spaces from the browser's/ProseMirror's
 * default HTML whitespace collapsing, which otherwise silently drops
 * indentation (e.g. a pretty-printed JSON body) the moment content
 * round-trips through `plainTextChunksToEditor` -- every edit re-renders via
 * `editor.commands.setContent`, so uncorrected this doesn't just affect the
 * initial paint. `editorToPlainTextChunks` undoes this on the way back out.
 */
function preserveLeadingWhitespace(line: string): string {
  return line.replace(/^ +/, (spaces) => '&nbsp;'.repeat(spaces.length));
}

/**
 * Turns template text into chunks: a text chunk per run between parts, a
 * variable chunk per `{{node.name}}` with its source filled by `resolveSource`,
 * a user mention chunk per `@[userId:label]`. Returns null when a `{{` isn't a
 * reference in that form, unclosed included.
 *
 * TODO(tim/gio): delete once the agent's ops carry chunks.
 */
export function templateToChunks(
  template: string,
  resolveSource: (ref: VariableRef) => VariableRef
): TextTemplateChunk[] | null {
  const chunks: TextTemplateChunk[] = [];
  const regex = new RegExp(TEMPLATE_PART_PATTERN, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    if (!maybeAppendTextChunk(chunks, template.slice(lastIndex, match.index))) {
      return null;
    }
    const groups = match.groups!;
    if (groups.nodeId !== undefined) {
      const ref = parseVariableRefName(groups.nodeId, groups.name);
      chunks.push({ variable: resolveSource(ref) });
    } else {
      chunks.push({
        user_mention: { user_id: groups.userId, label: groups.label },
      });
    }
    lastIndex = regex.lastIndex;
  }

  if (!maybeAppendTextChunk(chunks, template.slice(lastIndex))) {
    return null;
  }
  return chunks;
}

/** The HTML element TipTap parses into a variable chip. */
export const VARIABLE_CHIP_SELECTOR = 'span[data-type="mention"]';

/** The HTML element TipTap parses into a user mention chip. */
export const USER_MENTION_CHIP_SELECTOR =
  'span[class="mention-node"][data-user-id]';

/**
 * Returns the user id and label a mention element holds. The label comes from
 * `data-label`, or the element's text without its "@" when that's empty.
 */
export function readUserMentionAttributes(element: Element): {
  userId: string;
  label: string;
} {
  let label = element.getAttribute('data-label') ?? '';
  if (label === '') {
    label = (element.textContent ?? '').replace('@', '').trim();
  }
  return { userId: element.getAttribute('data-user-id') ?? '', label };
}

/** Returns the user mention chunk a mention element holds. */
function readRichTextUserMention(element: Element): TextTemplateChunk {
  const { userId, label } = readUserMentionAttributes(element);
  return { user_mention: { user_id: userId, label } };
}

/**
 * Returns the variable chunk a chip element holds. A chip with no reference
 * attributes gives an empty reference, which validation reports.
 */
function readEditorVariableChip(element: HTMLElement): TextTemplateChunk {
  const attributes = VARIABLE_CHIP_REFERENCE_ATTRIBUTES;
  return {
    variable: chipReference({
      nodeId: attributes.nodeId.parseHTML(element),
      variableName: attributes.variableName.parseHTML(element),
      derivedVariableName: attributes.derivedVariableName.parseHTML(element),
      source: attributes.source.parseHTML(element),
    }),
  };
}

/** An element standing in for a chip while the HTML around it is split. */
const CHUNK_MARKER_TAG = 'logiblocks-chunk-marker';

/**
 * The TipTap attributes a variable chip holds its reference in. The chip also
 * has `id`, `label` and `shortLabel`, which say how it reads.
 */
export const VARIABLE_CHIP_REFERENCE_ATTRIBUTES = {
  nodeId: {
    default: '',
    parseHTML: (el: HTMLElement) => el.getAttribute('data-node-id') ?? '',
  },
  variableName: {
    default: '',
    parseHTML: (el: HTMLElement) => el.getAttribute('data-variable-name') ?? '',
  },
  derivedVariableName: {
    default: '',
    parseHTML: (el: HTMLElement) =>
      el.getAttribute('data-derived-variable-name') ?? '',
  },
  source: {
    default: VariableSource.SOURCE_UNSPECIFIED,
    parseHTML: (el: HTMLElement) => Number(el.getAttribute('data-source') ?? 0),
  },
};

/**
 * The reference a variable chip holds. A chip missing its reference
 * attributes, such as one pasted from another editor, gives an empty one.
 */
export function chipReference(attrs: Attrs): VariableRef {
  return {
    node_id: attrs.nodeId ?? '',
    variable_name: attrs.variableName ?? '',
    derived_variable_name: attrs.derivedVariableName ?? '',
    source: attrs.source ?? VariableSource.SOURCE_UNSPECIFIED,
  };
}

/** The attributes a variable chip stores its reference in. */
export function chipAttributes(ref: VariableRef): {
  nodeId: string;
  variableName: string;
  derivedVariableName: string;
  source: VariableSource;
} {
  return {
    nodeId: ref.node_id,
    variableName: ref.variable_name,
    derivedVariableName: ref.derived_variable_name,
    source: ref.source,
  };
}

/**
 * A string naming a reference, all four fields included, for looking one up
 * in a set. Two references give the same key only when they're equal.
 */
export function variableReferenceKey(ref: VariableRef): string {
  return JSON.stringify([
    ref.node_id,
    ref.variable_name,
    ref.derived_variable_name,
    ref.source,
  ]);
}

/**
 * The span TipTap parses into a variable chip. The four `data-` fields after
 * the labels are the reference the chip stores; `data-id` is a joined name
 * TipTap requires every chip to have.
 */
function variableChipHTML(
  ref: VariableRef,
  nodes: ReactFlowNode[],
  variableTriggerChar: string
): string {
  const fullLabel = formatVariableRef(ref, nodes);
  const shortLabel = formatVariableRef(ref, nodes, 'short');
  return (
    `<span data-type="mention"` +
    ` data-id="${escapeAttr(`${ref.node_id}.${variableRefName(ref)}`)}"` +
    ` data-label="${escapeAttr(fullLabel)}"` +
    ` data-short-label="${escapeAttr(shortLabel)}"` +
    ` data-node-id="${escapeAttr(ref.node_id)}"` +
    ` data-variable-name="${escapeAttr(ref.variable_name)}"` +
    ` data-derived-variable-name="${escapeAttr(ref.derived_variable_name)}"` +
    ` data-source="${ref.source}"` +
    ` title="${escapeAttr(fullLabel)}"` +
    ` data-mention-suggestion-char="${escapeAttr(variableTriggerChar)}"` +
    `>${escapeHTML(shortLabel)}</span>`
  );
}

/** The span TipTap parses into a user mention chip. */
function userMentionChipHTML(userId: string, label: string): string {
  return (
    `<span class="mention-node"` +
    ` data-user-id="${escapeAttr(userId)}"` +
    ` data-label="${escapeAttr(label)}"` +
    ` title="@${escapeAttr(label)}"` +
    `>@${escapeHTML(label)}</span>`
  );
}

/**
 * Turns chunks into the HTML TipTap renders: text escaped, with each newline
 * starting a new paragraph; a variable chunk as a variable chip; a user
 * mention chunk as a mention chip.
 */
export function plainTextChunksToEditor(
  chunks: TextTemplateChunk[],
  nodes: ReactFlowNode[],
  variableTriggerChar: string = '{'
): string {
  if (chunks.length === 0) {
    return '';
  }

  let result = '';
  for (const chunk of chunks) {
    if (chunk.text !== undefined) {
      result += escapeHTML(chunk.text);
    } else if (chunk.variable !== undefined) {
      result += variableChipHTML(chunk.variable, nodes, variableTriggerChar);
    } else if (chunk.user_mention !== undefined) {
      result += userMentionChipHTML(
        chunk.user_mention.user_id,
        chunk.user_mention.label
      );
    }
  }

  // Wrap in paragraphs for TipTap (newlines → separate <p> blocks). A blank
  // line is an empty paragraph: a <br> in it would be read back as a line
  // break, adding a newline every time the field is opened and saved.
  const lines = result.split('\n').map(preserveLeadingWhitespace);
  return lines.map((line) => `<p>${line}</p>`).join('');
}

/**
 * Turns a rich-text field's chunks into the HTML TipTap renders: text chunks
 * joined as the markup they hold, a variable chunk as a variable chip, a user
 * mention chunk as a mention chip.
 */
export function richTextChunksToEditor(
  chunks: TextTemplateChunk[],
  nodes: ReactFlowNode[],
  variableTriggerChar: string
): string {
  let result = '';
  for (const chunk of chunks) {
    if (chunk.text !== undefined) {
      result += chunk.text;
    } else if (chunk.variable !== undefined) {
      result += variableChipHTML(chunk.variable, nodes, variableTriggerChar);
    } else if (chunk.user_mention !== undefined) {
      result += userMentionChipHTML(
        chunk.user_mention.user_id,
        chunk.user_mention.label
      );
    }
  }
  return result;
}

/**
 * Reads the editor's content as chunks: a variable chip becomes a variable
 * chunk from the reference it carries, a mention chip a user mention chunk,
 * and everything between two chips one text chunk, newlines included. An
 * empty editor gives no chunks.
 */
export function editorToPlainTextChunks(editor: Editor): TextTemplateChunk[] {
  const chunks: TextTemplateChunk[] = [];
  let text = '';
  const flushText = () => {
    if (text !== '') {
      chunks.push({ text });
      text = '';
    }
  };

  editor.state.doc.forEach((blockNode, _offset, index) => {
    if (index > 0) {
      text += '\n';
    }
    blockNode.forEach((child) => {
      if (child.type.name === 'mention') {
        flushText();
        chunks.push({ variable: chipReference(child.attrs) });
      } else if (child.type.name === 'userMention') {
        flushText();
        chunks.push({
          user_mention: { user_id: child.attrs.id, label: child.attrs.label },
        });
      } else if (child.type.name === 'hardBreak') {
        text += '\n';
      } else if (child.isText) {
        // Undo preserveLeadingWhitespace's &nbsp; substitution.
        text += (child.text ?? '').replace(/\u00A0/g, ' ');
      }
    });
  });
  flushText();

  return chunks;
}

/** Returns the chunk a chip or mention element in the editor's HTML holds. */
function readEditorChip(element: HTMLElement): TextTemplateChunk {
  if (element.matches(USER_MENTION_CHIP_SELECTOR)) {
    return readRichTextUserMention(element);
  }
  return readEditorVariableChip(element);
}

/**
 * Reads a rich-text editor's content as chunks: a chunk per variable chip and
 * mention chip, and the HTML between them as text chunks, kept as written. An
 * empty editor gives no chunks.
 */
export function editorToRichTextChunks(editor: Editor): TextTemplateChunk[] {
  if (editor.isEmpty) {
    return [];
  }
  const body = new DOMParser().parseFromString(
    editor.getHTML(),
    'text/html'
  ).body;

  // Swap each chip for a marker, keeping its chunk, so the HTML splits there.
  const chipChunks: TextTemplateChunk[] = [];
  const chips = body.querySelectorAll<HTMLElement>(
    `${VARIABLE_CHIP_SELECTOR}, ${USER_MENTION_CHIP_SELECTOR}`
  );
  for (const chip of Array.from(chips)) {
    chipChunks.push(readEditorChip(chip));
    chip.replaceWith(body.ownerDocument.createElement(CHUNK_MARKER_TAG));
  }

  const chunks: TextTemplateChunk[] = [];
  const marker = `<${CHUNK_MARKER_TAG}></${CHUNK_MARKER_TAG}>`;
  for (const [index, run] of body.innerHTML.split(marker).entries()) {
    if (run !== '') {
      chunks.push({ text: run });
    }
    if (index < chipChunks.length) {
      chunks.push(chipChunks[index]);
    }
  }
  return chunks;
}

/** Returns the user ids the chunks' user mentions name, each once, in order. */
export function getMentionedUserIds(chunks: TextTemplateChunk[]): string[] {
  const userIds: string[] = [];
  for (const chunk of chunks) {
    const userId = chunk.user_mention?.user_id;
    if (userId !== undefined && !userIds.includes(userId)) {
      userIds.push(userId);
    }
  }
  return userIds;
}

/** Whether two chunk lists hold the same parts, in the same order. */
export function chunksEqual(
  a: TextTemplateChunk[],
  b: TextTemplateChunk[]
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((chunk, i) => {
    const other = b[i];
    if (chunk.text !== undefined || other.text !== undefined) {
      return chunk.text === other.text;
    }
    if (chunk.variable !== undefined && other.variable !== undefined) {
      return (
        chunk.variable.node_id === other.variable.node_id &&
        chunk.variable.variable_name === other.variable.variable_name &&
        chunk.variable.derived_variable_name ===
          other.variable.derived_variable_name &&
        chunk.variable.source === other.variable.source
      );
    }
    if (chunk.user_mention !== undefined && other.user_mention !== undefined) {
      return (
        chunk.user_mention.user_id === other.user_mention.user_id &&
        chunk.user_mention.label === other.user_mention.label
      );
    }
    return false;
  });
}

/**
 * Returns the chunks' text joined, or null if any chunk is a variable or a
 * mention, whose text is known only at run time.
 */
export function getPlainTemplateText(
  chunks: TextTemplateChunk[]
): string | null {
  let text = '';
  for (const chunk of chunks) {
    if (chunk.text === undefined) {
      return null;
    }
    text += chunk.text;
  }
  return text;
}

// U+E000 and U+E001, private-use characters no author types. They bracket a
// chunk's index in the text `transformTemplateText` passes to its transform.
const CHUNK_INDEX_MARKER_START = String.fromCodePoint(0xe000);
const CHUNK_INDEX_MARKER_END = String.fromCodePoint(0xe001);

/**
 * Returns the chunks with their text rewritten by `transform`. `transform`
 * receives the text with a marker in place of each variable and mention, and
 * each chunk goes back where its marker ends up. Returns null if `transform`
 * drops or repeats a marker, or a text chunk already holds a marker character.
 */
export function transformTemplateText(
  chunks: TextTemplateChunk[],
  transform: (markedText: string) => string
): TextTemplateChunk[] | null {
  // Swap each variable and mention chunk for a marker holding its index.
  let markedText = '';
  const markedIndices: number[] = [];
  for (const [index, chunk] of chunks.entries()) {
    if (chunk.text === undefined) {
      markedText += `${CHUNK_INDEX_MARKER_START}${index}${CHUNK_INDEX_MARKER_END}`;
      markedIndices.push(index);
    } else if (
      chunk.text.includes(CHUNK_INDEX_MARKER_START) ||
      chunk.text.includes(CHUNK_INDEX_MARKER_END)
    ) {
      return null;
    } else {
      markedText += chunk.text;
    }
  }

  const transformedText = transform(markedText);

  // Split at the markers to put the chunks back.
  const transformedChunks: TextTemplateChunk[] = [];
  const restoredIndices: number[] = [];
  const markerPattern = new RegExp(
    `${CHUNK_INDEX_MARKER_START}(\\d+)${CHUNK_INDEX_MARKER_END}`,
    'g'
  );
  let textStart = 0;
  for (const match of transformedText.matchAll(markerPattern)) {
    const text = transformedText.slice(textStart, match.index);
    if (text !== '') {
      transformedChunks.push({ text });
    }
    const index = Number(match[1]);
    transformedChunks.push(chunks[index]);
    restoredIndices.push(index);
    textStart = match.index + match[0].length;
  }
  const trailingText = transformedText.slice(textStart);
  if (trailingText !== '') {
    transformedChunks.push({ text: trailingText });
  }

  // Each variable and mention chunk comes back exactly once, in any order.
  const sortedRestoredIndices = [...restoredIndices].sort((a, b) => a - b);
  if (sortedRestoredIndices.join(',') !== markedIndices.join(',')) {
    return null;
  }
  return transformedChunks;
}
